import { useState, useEffect, useRef, useCallback } from "react";
import { C } from "../utils/tokens";
import { Tag } from "./primitives";
import { TEMPLATES } from "../data/templates";
import { renderCV, DEMO_DATA } from "../utils/renderCV";

// ── UTILS ─────────────────────────────────────────────────────────────────────
const hexToRgb = (hex) => {
  if (!hex || hex.length < 7) return [245, 197, 24];
  try {
    return [
      parseInt(hex.slice(1, 3), 16),
      parseInt(hex.slice(3, 5), 16),
      parseInt(hex.slice(5, 7), 16),
    ];
  } catch {
    return [245, 197, 24];
  }
};
const lerp = (a, b, t) => a + (b - a) * t;
const safeR = (v) => Math.max(0.001, isNaN(v) ? 0.001 : v);

// ── MEGA CANVAS — particelle 3D + aurora + warp + cursor trail ────────────────
const MegaCanvas = ({ activeColor }) => {
  const canvasRef = useRef();
  const colorRef = useRef([245, 197, 24]);
  const targetColorRef = useRef([245, 197, 24]);
  const mouseRef = useRef({ x: 0.5, y: 0.5, vx: 0, vy: 0 });
  const trailRef = useRef([]);

  useEffect(() => {
    const [r, g, b] = hexToRgb(activeColor);
    targetColorRef.current = [r, g, b];
  }, [activeColor]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId,
      t = 0;
    let lastMX = 0,
      lastMY = 0;

    const dpr = Math.min(window.devicePixelRatio, 2);
    const resize = () => {
      canvas.width = canvas.offsetWidth * dpr;
      canvas.height = canvas.offsetHeight * dpr;
      ctx.scale(dpr, dpr);
    };
    resize();
    window.addEventListener("resize", resize);
    const W = () => canvas.offsetWidth,
      H = () => canvas.offsetHeight;

    // Mouse tracking
    const onMouse = (e) => {
      const nx = e.clientX / window.innerWidth;
      const ny = e.clientY / window.innerHeight;
      mouseRef.current.vx = nx - mouseRef.current.x;
      mouseRef.current.vy = ny - mouseRef.current.y;
      mouseRef.current.x = nx;
      mouseRef.current.y = ny;
      trailRef.current.push({ x: e.clientX, y: e.clientY, life: 1 });
      if (trailRef.current.length > 32) trailRef.current.shift();
    };
    window.addEventListener("mousemove", onMouse);

    // Noise
    const p = new Uint8Array(512);
    for (let i = 0; i < 256; i++) p[i] = i;
    for (let i = 255; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [p[i], p[j]] = [p[j], p[i]];
    }
    for (let i = 0; i < 256; i++) p[256 + i] = p[i];
    const fade = (t) => t * t * t * (t * (t * 6 - 15) + 10);
    const grad = (h, x, y) => {
      const v = h & 3,
        u = v < 2 ? x : y,
        w = v < 2 ? y : x;
      return (v & 1 ? -u : u) + (v & 2 ? -w : w);
    };
    const noise = (x, y) => {
      const X = Math.floor(x) & 255,
        Y = Math.floor(y) & 255;
      x -= Math.floor(x);
      y -= Math.floor(y);
      const u = fade(x),
        v = fade(y),
        a = p[X] + Y,
        b = p[X + 1] + Y;
      return lerp(
        lerp(grad(p[a], x, y), grad(p[b], x - 1, y), u),
        lerp(grad(p[a + 1], x, y - 1), grad(p[b + 1], x - 1, y - 1), u),
        v,
      );
    };
    const fbm = (x, y, o = 4) => {
      let v = 0,
        a = 0.5,
        f = 1,
        m = 0;
      for (let i = 0; i < o; i++) {
        v += noise(x * f, y * f) * a;
        m += a;
        a *= 0.5;
        f *= 2.1;
      }
      return v / m;
    };

    // 3D Particles
    const NPARTICLES = 200;
    const particles = Array.from({ length: NPARTICLES }, (_, i) => ({
      x: Math.random() * 2 - 1,
      y: Math.random() * 2 - 1,
      z: Math.random() * 2 - 1,
      vx: (Math.random() - 0.5) * 0.001,
      vy: (Math.random() - 0.5) * 0.001,
      vz: (Math.random() - 0.5) * 0.001,
      size: 0.5 + Math.random() * 1.5,
      phase: Math.random() * Math.PI * 2,
      type: i % 4, // 0=dot, 1=star, 2=cross, 3=ring
    }));

    // Warp lines (spaceship hyperspace)
    const NWARP = 80;
    const warpLines = Array.from({ length: NWARP }, () => ({
      angle: Math.random() * Math.PI * 2,
      r: 0.05 + Math.random() * 0.95,
      speed: 0.002 + Math.random() * 0.006,
      len: 0.02 + Math.random() * 0.08,
      alpha: 0.1 + Math.random() * 0.4,
    }));

    const draw = () => {
      const w = W(),
        h = H();
      if (w < 1 || h < 1) {
        animId = requestAnimationFrame(draw);
        return;
      }

      // Lerp color
      const [tr, tg, tb] = targetColorRef.current;
      const [cr, cg, cb] = colorRef.current;
      colorRef.current = [
        lerp(cr, tr, 0.03),
        lerp(cg, tg, 0.03),
        lerp(cb, tb, 0.03),
      ];
      const [r, g, b] = colorRef.current;

      // Clear with slight trail
      ctx.fillStyle = "rgba(8,9,9,0.12)";
      ctx.fillRect(0, 0, w, h);

      const mx = mouseRef.current.x,
        my = mouseRef.current.y;
      const cx = w * mx,
        cy = h * my;

      // ── AURORA BANDS ──
      for (let i = 0; i < 6; i++) {
        const bx =
          w * (0.1 + i * 0.16) +
          Math.sin(t * 0.2 + i * 1.1) * w * 0.08 +
          (mx - 0.5) * w * 0.12;
        const by =
          h * (0.15 + i * 0.14) +
          Math.cos(t * 0.15 + i * 0.9) * h * 0.06 +
          (my - 0.5) * h * 0.08;
        const br = safeR(
          w * (0.15 + i * 0.04) * (1 + Math.sin(t * 0.3 + i) * 0.2),
        );
        const ba = (0.025 + Math.sin(t * 0.4 + i) * 0.01) * (1.5 - i * 0.15);
        const ag = ctx.createRadialGradient(bx, by, 0, bx, by, br);
        ag.addColorStop(0, `rgba(${r | 0},${g | 0},${b | 0},${ba * 4})`);
        ag.addColorStop(0.4, `rgba(${r | 0},${g | 0},${b | 0},${ba})`);
        ag.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = ag;
        ctx.beginPath();
        ctx.ellipse(
          bx,
          by,
          br,
          safeR(h * 0.06 * (1 + Math.sin(t * 0.25 + i) * 0.3)),
          Math.sin(t * 0.08 + i) * 0.4,
          0,
          Math.PI * 2,
        );
        ctx.fill();
      }

      // ── WARP LINES ──
      warpLines.forEach((line) => {
        line.r = (line.r + line.speed) % 1;
        const cr2 = line.r,
          dist = cr2 * Math.min(w, h) * 0.55;
        const x1 = w * 0.5 + Math.cos(line.angle) * dist;
        const y1 = h * 0.5 + Math.sin(line.angle) * dist * 0.6;
        const x2 = w * 0.5 + Math.cos(line.angle) * (dist * (1 - line.len));
        const y2 =
          h * 0.5 + Math.sin(line.angle) * (dist * (1 - line.len)) * 0.6;
        const a = line.alpha * (1 - cr2) * 0.5;
        if (a < 0.01) {
          line.r = 0;
          return;
        }
        ctx.strokeStyle = `rgba(${r | 0},${g | 0},${b | 0},${a})`;
        ctx.lineWidth = 0.5 + cr2 * 1.5;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      });

      // ── 3D PARTICLES ──
      particles.forEach((pt) => {
        // Orbit + noise drift
        pt.x += pt.vx + fbm(pt.x + t * 0.1, pt.y + t * 0.08) * 0.0008;
        pt.y += pt.vy + fbm(pt.y + t * 0.1, pt.z + t * 0.09) * 0.0008;
        pt.z += pt.vz;
        // Wrap
        ["x", "y", "z"].forEach((k) => {
          if (pt[k] > 1) pt[k] = -1;
          if (pt[k] < -1) pt[k] = 1;
        });
        // Project 3D → 2D (simple perspective)
        const fov = 2.5,
          zOff = fov + pt.z;
        if (zOff < 0.1) return;
        const px = w * (0.5 + (pt.x / zOff) * 0.8);
        const py = h * (0.5 + (pt.y / zOff) * 0.6);
        const ps = safeR(
          pt.size * (fov / zOff) * (1 + Math.sin(t * 0.5 + pt.phase) * 0.3),
        );
        const pdist = Math.sqrt((px - cx) ** 2 + (py - cy) ** 2);
        const pBright = Math.max(0, 1 - pdist / (w * 0.35));
        const pa = (0.2 + pBright * 0.7) * (0.5 + pt.z * 0.5);
        if (pa < 0.02) return;
        const pr = r | 0,
          pg = g | 0,
          pb2 = b | 0;
        ctx.fillStyle = `rgba(${pr},${pg},${pb2},${pa})`;

        if (pt.type === 0) {
          // dot
          ctx.beginPath();
          ctx.arc(px, py, ps, 0, Math.PI * 2);
          ctx.fill();
        } else if (pt.type === 1 && ps > 0.8) {
          // star
          ctx.save();
          ctx.translate(px, py);
          ctx.rotate(t * 0.3 + pt.phase);
          ctx.beginPath();
          for (let i = 0; i < 4; i++) {
            ctx.moveTo(0, 0);
            ctx.lineTo(
              ps * 2 * Math.cos((i * Math.PI) / 2),
              ps * 2 * Math.sin((i * Math.PI) / 2),
            );
          }
          ctx.strokeStyle = `rgba(${pr},${pg},${pb2},${pa * 0.6})`;
          ctx.lineWidth = 0.5;
          ctx.stroke();
          ctx.restore();
          ctx.beginPath();
          ctx.arc(px, py, safeR(ps * 0.5), 0, Math.PI * 2);
          ctx.fill();
        } else if (pt.type === 2 && pBright > 0.3) {
          // glow ring
          ctx.strokeStyle = `rgba(${pr},${pg},${pb2},${pa * 0.4})`;
          ctx.lineWidth = 0.5;
          ctx.beginPath();
          ctx.arc(px, py, safeR(ps * 2), 0, Math.PI * 2);
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(px, py, ps, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.beginPath();
          ctx.arc(px, py, ps, 0, Math.PI * 2);
          ctx.fill();
        }

        // Connections between close particles
        if (pBright > 0.2) {
          particles.forEach((pt2) => {
            const px2 = w * (0.5 + (pt2.x / (fov + pt2.z)) * 0.8);
            const py2 = h * (0.5 + (pt2.y / (fov + pt2.z)) * 0.6);
            const d = Math.sqrt((px - px2) ** 2 + (py - py2) ** 2);
            if (d < 80 && d > 2) {
              ctx.strokeStyle = `rgba(${pr},${pg},${pb2},${(1 - d / 80) * pa * 0.3})`;
              ctx.lineWidth = 0.4;
              ctx.beginPath();
              ctx.moveTo(px, py);
              ctx.lineTo(px2, py2);
              ctx.stroke();
            }
          });
        }
      });

      // ── CURSOR TRAIL ──
      trailRef.current = trailRef.current
        .map((pt) => ({ ...pt, life: pt.life - 0.06 }))
        .filter((pt) => pt.life > 0);
      trailRef.current.forEach((pt, i) => {
        const next = trailRef.current[i + 1];
        if (!next) return;
        const a = pt.life * 0.5;
        ctx.strokeStyle = `rgba(${r | 0},${g | 0},${b | 0},${a})`;
        ctx.lineWidth = pt.life * 6;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(pt.x, pt.y);
        ctx.lineTo(next.x, next.y);
        ctx.stroke();
      });
      // Cursor glow
      if (trailRef.current.length > 0) {
        const last = trailRef.current[trailRef.current.length - 1];
        const cGlow = ctx.createRadialGradient(
          last.x,
          last.y,
          0,
          last.x,
          last.y,
          safeR(80),
        );
        cGlow.addColorStop(0, `rgba(${r | 0},${g | 0},${b | 0},0.25)`);
        cGlow.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = cGlow;
        ctx.beginPath();
        ctx.arc(last.x, last.y, 80, 0, Math.PI * 2);
        ctx.fill();
      }

      // ── BIG CENTRAL NEBULA ──
      const ng = ctx.createRadialGradient(
        w * 0.5,
        h * 0.4,
        0,
        w * 0.5,
        h * 0.4,
        safeR(Math.min(w, h) * 0.5),
      );
      ng.addColorStop(0, `rgba(${r | 0},${g | 0},${b | 0},0.04)`);
      ng.addColorStop(0.5, `rgba(${r | 0},${g | 0},${b | 0},0.015)`);
      ng.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = ng;
      ctx.fillRect(0, 0, w, h);

      t += 0.016;
      animId = requestAnimationFrame(draw);
    };

    draw();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMouse);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 0,
      }}
    />
  );
};

// ── TILT 3D CARD ──────────────────────────────────────────────────────────────
const TiltCard = ({ t, onPreview, onSelect, isHovered, onHover, onLeave }) => {
  const cardRef = useRef();
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [shine, setShine] = useState({ x: 50, y: 50 });
  const rendered = renderCV(
    t,
    DEMO_DATA.info,
    DEMO_DATA.esperienze,
    DEMO_DATA.formazione,
    DEMO_DATA.competenze,
    null,
    true,
  );

  const onMouseMove = useCallback((e) => {
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    setTilt({ x: (py - 0.5) * 16, y: (px - 0.5) * -16 });
    setShine({ x: px * 100, y: py * 100 });
  }, []);

  const onMouseLeaveCard = useCallback(() => {
    setTilt({ x: 0, y: 0 });
    setShine({ x: 50, y: 50 });
    onLeave();
  }, [onLeave]);

  return (
    <div
      ref={cardRef}
      onMouseMove={onMouseMove}
      onMouseEnter={onHover}
      onMouseLeave={onMouseLeaveCard}
      style={{
        borderRadius: 20,
        overflow: "hidden",
        cursor: "pointer",
        transform: `perspective(800px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) ${isHovered ? "scale(1.04) translateY(-10px)" : "scale(1)"}`,
        transition: isHovered
          ? "transform 0.1s ease"
          : "transform 0.5s cubic-bezier(0.34,1.56,0.64,1)",
        boxShadow: isHovered
          ? `0 30px 80px ${t.color}44, 0 0 0 1px ${t.color}33, inset 0 1px 0 rgba(255,255,255,0.1)`
          : `0 4px 24px rgba(0,0,0,0.5)`,
        background: "rgba(8,9,9,0.8)",
        border: `1px solid ${isHovered ? t.color + "55" : "rgba(255,255,255,0.06)"}`,
        backdropFilter: "blur(24px)",
        position: "relative",
        willChange: "transform",
      }}
    >
      {/* Shine effect */}
      {isHovered && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 5,
            pointerEvents: "none",
            background: `radial-gradient(circle at ${shine.x}% ${shine.y}%, rgba(255,255,255,0.08) 0%, transparent 60%)`,
            borderRadius: 20,
          }}
        />
      )}

      {/* Top glow bar */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          zIndex: 4,
          background: isHovered
            ? `linear-gradient(to right, transparent 0%, ${t.color} 50%, transparent 100%)`
            : `linear-gradient(to right, transparent, ${t.color}33, transparent)`,
          transition: "background 0.4s",
          boxShadow: isHovered ? `0 0 20px ${t.color}` : "none",
        }}
      />

      {/* CV Preview */}
      <div
        style={{
          position: "relative",
          overflow: "hidden",
          height: 215,
          background: `linear-gradient(135deg,${t.color}0a,rgba(0,0,0,0.6))`,
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: "50%",
            width: "700px",
            transform: `translateX(-50%) scale(${isHovered ? 0.355 : 0.34})`,
            transformOrigin: "top center",
            pointerEvents: "none",
            transition: "transform 0.4s ease",
          }}
        >
          {rendered}
        </div>
        <div
          onClick={(e) => {
            e.stopPropagation();
            onPreview(t);
          }}
          style={{
            position: "absolute",
            inset: 0,
            background: `linear-gradient(to bottom,transparent 40%,rgba(8,9,9,${isHovered ? 0.3 : 0.75}) 100%)`,
            transition: "background 0.3s",
          }}
        />
        <div style={{ position: "absolute", top: 10, right: 10, zIndex: 3 }}>
          <Tag color={t.color}>{t.cat}</Tag>
        </div>
        <div
          style={{
            position: "absolute",
            top: 10,
            left: 10,
            zIndex: 3,
            width: 34,
            height: 34,
            borderRadius: 10,
            background: isHovered ? `${t.color}33` : `${t.color}18`,
            border: `1px solid ${t.color}${isHovered ? "66" : "33"}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 15,
            color: t.color,
            backdropFilter: "blur(8px)",
            transition: "all 0.3s",
            boxShadow: isHovered ? `0 0 16px ${t.color}44` : "none",
          }}
        >
          {t.icon}
        </div>
      </div>

      {/* Info */}
      <div style={{ padding: "14px 16px 16px" }}>
        <div
          style={{
            fontFamily: "'Syne',sans-serif",
            fontWeight: 800,
            fontSize: 16,
            color: C.text,
            marginBottom: 4,
            letterSpacing: "-0.01em",
          }}
        >
          {t.name}
        </div>
        <div
          style={{
            fontSize: 11.5,
            color: C.muted,
            lineHeight: 1.55,
            marginBottom: 13,
            minHeight: 34,
          }}
        >
          {t.sub}
        </div>
        <div style={{ display: "flex", gap: 7 }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPreview(t);
            }}
            style={{
              flex: 1,
              padding: "8px 0",
              borderRadius: 8,
              border: `1px solid ${isHovered ? t.color + "44" : "rgba(255,255,255,0.07)"}`,
              background: "transparent",
              color: isHovered ? t.color : C.muted,
              fontSize: 11.5,
              fontFamily: "'Syne',sans-serif",
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.2s",
            }}
          >
            👁 Anteprima
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSelect(t);
            }}
            style={{
              flex: 1,
              padding: "8px 0",
              borderRadius: 8,
              border: "none",
              background: `linear-gradient(135deg,${t.color},${t.color}99)`,
              color: "#000",
              fontSize: 11.5,
              fontFamily: "'Syne',sans-serif",
              fontWeight: 800,
              cursor: "pointer",
              transition: "all 0.25s cubic-bezier(0.34,1.56,0.64,1)",
              boxShadow: isHovered ? `0 4px 20px ${t.color}55` : "none",
              transform: isHovered ? "scale(1.02)" : "scale(1)",
            }}
          >
            Scegli →
          </button>
        </div>
      </div>
    </div>
  );
};

// ── MODAL ─────────────────────────────────────────────────────────────────────
const TemplateModal = ({ tpl, onClose, onSelect }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);

    window.addEventListener("resize", onResize);

    return () => window.removeEventListener("resize", onResize);
  }, []);

  const rendered = renderCV(
    tpl,
    DEMO_DATA.info,
    DEMO_DATA.esperienze,
    DEMO_DATA.formazione,
    DEMO_DATA.competenze,
    null,
    true,
  );

  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") onClose();
    };

    window.addEventListener("keydown", h);

    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", h);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <>
      {/* BACKDROP */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 8888,

          background: isMobile ? "rgba(0,0,0,0.92)" : "rgba(0,0,0,0.88)",

          backdropFilter: isMobile ? "blur(10px)" : "blur(16px)",
        }}
      />

      {/* MODAL */}
      <div
        style={{
          position: "fixed",

          top: isMobile ? 0 : "50%",
          left: "50%",

          transform: isMobile ? "translateX(-50%)" : "translate(-50%,-50%)",

          zIndex: 8889,

          width: isMobile ? "100vw" : "min(95vw,1040px)",

          height: isMobile ? "100dvh" : "auto",

          maxHeight: isMobile ? "100dvh" : "90vh",

          background:
            "linear-gradient(135deg,rgba(10,10,8,0.99),rgba(8,9,9,0.99))",

          border: `1px solid ${tpl.color}44`,

          borderRadius: isMobile ? 0 : 28,

          overflow: "hidden",

          boxShadow: isMobile
            ? "none"
            : `0 0 120px ${tpl.color}22,
               0 0 240px ${tpl.color}0a,
               0 40px 120px rgba(0,0,0,0.9)`,

          display: "flex",

          flexDirection: isMobile ? "column" : "row",

          animation: "fadeUp 0.25s ease",
        }}
      >
        {/* TOP GLOW */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 2,

            background: `linear-gradient(to right,
              transparent,
              ${tpl.color},
              transparent)`,

            boxShadow: `0 0 30px ${tpl.color}`,

            zIndex: 10,
          }}
        />

        {/* CLOSE */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",

            top: isMobile ? 16 : 20,
            right: isMobile ? 16 : 20,

            width: isMobile ? 44 : 38,
            height: isMobile ? 44 : 38,

            zIndex: 50,

            background: "rgba(255,255,255,0.06)",

            border: "1px solid rgba(255,255,255,0.1)",

            borderRadius: 12,

            color: C.muted,

            fontSize: 18,

            cursor: "pointer",

            display: "flex",
            alignItems: "center",
            justifyContent: "center",

            backdropFilter: "blur(12px)",
          }}
        >
          ✕
        </button>

        {/* PREVIEW */}
        <div
          style={{
            flex: isMobile ? "unset" : "0 0 54%",

            height: isMobile ? "48vh" : "auto",

            overflowY: "auto",

            padding: isMobile ? 18 : 32,

            background: "rgba(0,0,0,0.2)",

            borderRight: isMobile ? "none" : `1px solid ${tpl.color}18`,

            borderBottom: isMobile ? `1px solid ${tpl.color}18` : "none",
          }}
        >
          <div
            style={{
              fontSize: 9.5,
              color: C.muted,

              fontFamily: "'DM Mono',monospace",

              letterSpacing: "0.12em",

              textTransform: "uppercase",

              marginBottom: 16,

              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <div
              style={{
                width: 6,
                height: 6,

                borderRadius: "50%",

                background: tpl.color,

                boxShadow: `0 0 10px ${tpl.color}`,

                animation: "pulse 2s infinite",
              }}
            />
            Anteprima reale
          </div>

          {/* SCALE FIX MOBILE */}
          <div
            style={{
              width: "100%",

              display: "flex",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                width: isMobile ? 760 : "100%",

                transform: isMobile ? "scale(0.58)" : "scale(1)",

                transformOrigin: "top center",

                marginBottom: isMobile ? -240 : 0,
              }}
            >
              {rendered}
            </div>
          </div>
        </div>

        {/* INFO */}
        <div
          style={{
            flex: 1,

            display: "flex",
            flexDirection: "column",

            overflowY: "auto",

            padding: isMobile ? "22px 18px 140px" : "36px 32px",
          }}
        >
          {/* ICON */}
          <div
            style={{
              display: "flex",
              alignItems: "center",

              gap: 14,

              marginBottom: 20,

              marginTop: isMobile ? 28 : 0,
            }}
          >
            <div
              style={{
                width: isMobile ? 54 : 60,
                height: isMobile ? 54 : 60,

                borderRadius: 16,

                background: `${tpl.color}18`,

                border: `2px solid ${tpl.color}44`,

                display: "flex",
                alignItems: "center",
                justifyContent: "center",

                fontSize: isMobile ? 24 : 28,

                color: tpl.color,

                boxShadow: `0 0 30px ${tpl.color}33`,
              }}
            >
              {tpl.icon}
            </div>

            <Tag color={tpl.color}>{tpl.cat}</Tag>
          </div>

          {/* TITLE */}
          <h2
            style={{
              fontFamily: "'Syne',sans-serif",

              fontWeight: 900,

              fontSize: isMobile ? 42 : 32,

              lineHeight: 0.92,

              color: C.text,

              marginBottom: 10,

              letterSpacing: "-0.04em",
            }}
          >
            {tpl.name}
          </h2>

          {/* SUB */}
          <p
            style={{
              color: C.muted,

              fontSize: isMobile ? 15 : 14,

              lineHeight: 1.7,

              marginBottom: 26,
            }}
          >
            {tpl.sub}
          </p>

          {/* FEATURES */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            {[
              "Layout ottimizzato per il tuo settore",
              "Compatibile con ATS e recruiter",
              "Supporto foto profilo",
              "Export PDF professionale",
              "Competenze suggerite per settore",
            ].map((f, i) => (
              <div
                key={i}
                style={{
                  display: "flex",

                  gap: 12,

                  alignItems: "center",

                  padding: isMobile ? "12px 14px" : "9px 14px",

                  background: "rgba(255,255,255,0.03)",

                  borderRadius: 14,

                  border: "1px solid rgba(255,255,255,0.05)",
                }}
              >
                <div
                  style={{
                    width: 24,
                    height: 24,

                    borderRadius: 8,

                    background: `${tpl.color}22`,

                    border: `1px solid ${tpl.color}44`,

                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",

                    flexShrink: 0,
                  }}
                >
                  <span
                    style={{
                      color: tpl.color,
                      fontSize: 11,
                    }}
                  >
                    ✓
                  </span>
                </div>

                <span
                  style={{
                    fontSize: isMobile ? 14 : 13,
                    color: C.text,
                    lineHeight: 1.45,
                  }}
                >
                  {f}
                </span>
              </div>
            ))}
          </div>

          {/* CTA MOBILE STICKY */}
          <div
            style={{
              position: isMobile ? "fixed" : "sticky",

              bottom: 0,

              left: 0,
              right: 0,

              padding: isMobile
                ? "16px 18px calc(16px + env(safe-area-inset-bottom))"
                : "24px 0 0",

              background: isMobile
                ? "linear-gradient(to top, rgba(8,9,9,0.98), rgba(8,9,9,0.7), transparent)"
                : "transparent",

              backdropFilter: isMobile ? "blur(18px)" : "none",

              marginTop: 30,
            }}
          >
            <button
              onClick={() => {
                onSelect(tpl);
                onClose();
              }}
              style={{
                width: "100%",

                minHeight: isMobile ? 58 : "unset",

                padding: "17px 0",

                borderRadius: 16,

                border: "none",

                background: `linear-gradient(135deg,
                  ${tpl.color},
                  ${tpl.color}bb)`,

                color: "#000",

                fontFamily: "'Syne',sans-serif",

                fontWeight: 900,

                fontSize: isMobile ? 18 : 16,

                cursor: "pointer",

                boxShadow: `0 8px 40px ${tpl.color}55`,

                letterSpacing: "-0.01em",
              }}
            >
              Usa questo template →
            </button>

            {!isMobile && (
              <div
                style={{
                  textAlign: "center",
                  marginTop: 10,
                  fontSize: 11,
                  color: C.muted,
                }}
              >
                Premi ESC per chiudere · Puoi cambiare template in qualsiasi
                momento
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// ── MAIN ──────────────────────────────────────────────────────────────────────
const TemplatePicker = ({ onSelect }) => {
  const [hover, setHover] = useState(null);
  const [filter, setFilter] = useState("Tutti");
  const [preview, setPreview] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [titleTemplate, setTitleTemplate] = useState(null);

  useEffect(() => {
    setTimeout(() => setMounted(true), 80);
  }, []);

  const cats = ["Tutti", ...[...new Set(TEMPLATES.map((t) => t.cat))]];
  const visible =
    filter === "Tutti" ? TEMPLATES : TEMPLATES.filter((t) => t.cat === filter);
  const hoveredTpl = TEMPLATES.find((t) => t.id === hover);
  const activeColor = hoveredTpl?.color || C.gold;

  useEffect(() => {
    if (hoveredTpl) setTitleTemplate(hoveredTpl);
  }, [hoveredTpl]);

  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        background: "#080909",
        overflow: "hidden",
      }}
    >
      <MegaCanvas activeColor={activeColor} />

      {/* Vignette */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 1,
          pointerEvents: "none",
          background:
            "radial-gradient(ellipse at 50% 50%, transparent 20%, rgba(8,9,9,0.5) 100%)",
        }}
      />

      {preview && (
        <TemplateModal
          tpl={preview}
          onClose={() => setPreview(null)}
          onSelect={onSelect}
        />
      )}

      <div
        style={{
          position: "relative",
          zIndex: 2,
          maxWidth: 1340,
          margin: "0 auto",
          padding: "72px 24px 130px",
        }}
      >
        {/* Hero */}
        <div style={{ textAlign: "center", marginBottom: 72 }}>
          <div
            style={{
              marginBottom: 22,
              opacity: mounted ? 1 : 0,
              transform: mounted ? "translateY(0)" : "translateY(24px)",
              transition: "all 0.7s ease",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                padding: "7px 20px 7px 10px",
                background: "rgba(245,197,24,0.07)",
                border: `1px solid ${C.goldBorder}`,
                borderRadius: 100,
              }}
            >
              <div
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: C.gold,
                  boxShadow: `0 0 10px ${C.gold}`,
                  animation: "pulse 2s infinite",
                }}
              />
              <span
                style={{
                  fontSize: 11,
                  color: C.gold,
                  fontFamily: "'DM Mono',monospace",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                }}
              >
                {TEMPLATES.length} template · scegli il tuo settore
              </span>
            </div>
          </div>

          <div
            style={{
              opacity: mounted ? 1 : 0,
              transform: mounted ? "translateY(0)" : "translateY(32px)",
              transition: "all 0.8s 0.1s ease",
            }}
          >
            <h1
              style={{
                fontFamily: "'Syne',sans-serif",
                fontWeight: 900,
                fontSize: "clamp(44px,6.5vw,88px)",
                lineHeight: 0.95,
                letterSpacing: "-0.05em",
                margin: "0 0 24px",
              }}
            >
              <span
                style={{
                  display: "block",
                  background:
                    "linear-gradient(135deg,#fff 40%,rgba(255,255,255,0.45))",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Scegli il formato
              </span>
              {/* <span
                style={{
                  display: "block",
                  fontFamily: "'Instrument Serif',serif",
                  fontStyle: "italic",
                  background: `linear-gradient(135deg,${activeColor} 20%,${activeColor}77)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  transition: "background 0.6s ease",
                  minHeight: "1.1em",
                }}
              >
                {titleTemplate
                  ? `${titleTemplate.name} — ${titleTemplate.cat}`
                  : "per il tuo settore."}
              </span> */}
            </h1>
          </div>
          {/* 
          <p
            style={{
              color: C.muted,
              fontSize: 16,
              maxWidth: 480,
              margin: "0 auto",
              lineHeight: 1.75,
              opacity: mounted ? 1 : 0,
              transition: "all 0.8s 0.2s ease",
            }}
          >
            Passa il mouse sulle card per vederle in azione.
            <br />
            Il titolo qui sopra cambia in tempo reale.
          </p> */}
        </div>

        {/* Filters */}
        <div
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            justifyContent: "center",
            marginBottom: 52,
            opacity: mounted ? 1 : 0,
            transition: "all 0.8s 0.3s ease",
          }}
        >
          {cats.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              style={{
                padding: "8px 20px",
                borderRadius: 100,
                border: `1px solid ${filter === c ? activeColor : "rgba(255,255,255,0.07)"}`,
                background:
                  filter === c ? `${activeColor}18` : "rgba(255,255,255,0.03)",
                color: filter === c ? activeColor : C.muted,
                fontSize: 12,
                fontFamily: "'Syne',sans-serif",
                fontWeight: 700,
                cursor: "pointer",
                transition: "all 0.25s",
                backdropFilter: "blur(8px)",
                boxShadow: filter === c ? `0 0 20px ${activeColor}22` : "none",
              }}
              onMouseEnter={(e) => {
                if (filter !== c) {
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)";
                  e.currentTarget.style.color = C.text;
                }
              }}
              onMouseLeave={(e) => {
                if (filter !== c) {
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)";
                  e.currentTarget.style.color = C.muted;
                }
              }}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill,minmax(255px,1fr))",
            gap: 20,
            opacity: mounted ? 1 : 0,
            transition: "all 0.8s 0.4s ease",
          }}
        >
          {visible.map((t) => (
            <TiltCard
              key={t.id}
              t={t}
              isHovered={hover === t.id}
              onHover={() => setHover(t.id)}
              onLeave={() => setHover(null)}
              onPreview={setPreview}
              onSelect={onSelect}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default TemplatePicker;
